import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { 
  VideoCallManagement, 
  VideoCallManagementCreateDto, 
  VideoCallManagementUpdateDto,
  VideoCallManagementResponse,
  VideoCallManagementLatestResponse
} from "../../modules/shared/model";
import { 
  getLatestVideoCallManagement as getLatestVideoCallManagementAction,
  getVideoCallManagement as getVideoCallManagementAction, 
  getVideoCallManagementById as getVideoCallManagementByIdAction,
  createVideoCallManagementAction,
  updateVideoCallManagementAction,
  deleteVideoCallManagementAction
} from "../Actions/VideoCallManagementActions";

// Async Thunks
export const fetchLatestVideoCallManagement = createAsyncThunk("videoCallManagement/fetchLatest", async (_, { rejectWithValue }) => {
  try { 
    return await getLatestVideoCallManagementAction(); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch latest video call management"); 
  }
});

export const fetchVideoCallManagement = createAsyncThunk("videoCallManagement/fetchAll", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
  try { 
    return await getVideoCallManagementAction(query); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch video call management"); 
  }
});

export const fetchVideoCallManagementById = createAsyncThunk("videoCallManagement/fetchById", async (id: string, { rejectWithValue }) => {
  try { 
    return await getVideoCallManagementByIdAction(id); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch video call management"); 
  }
});

export const createVideoCallManagement = createAsyncThunk("videoCallManagement/create", async (data: VideoCallManagementCreateDto, { rejectWithValue }) => {
  try { 
    return await createVideoCallManagementAction(data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to create video call management"); 
  }
});

export const updateVideoCallManagement = createAsyncThunk("videoCallManagement/update", async ({ id, data }: { id: string; data: VideoCallManagementUpdateDto }, { rejectWithValue }) => {
  try { 
    return await updateVideoCallManagementAction(id, data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to update video call management"); 
  }
});

export const deleteVideoCallManagement = createAsyncThunk("videoCallManagement/delete", async (id: string, { rejectWithValue }) => {
  try { 
    await deleteVideoCallManagementAction(id); 
    return id; 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to delete video call management"); 
  }
});

// State interface
interface VideoCallManagementState {
  // Latest settings (single item)
  latestSettings: VideoCallManagement | null;
  latestSettingsLoading: boolean;
  latestSettingsError: string | null;
  
  // All settings (paginated)
  settings: VideoCallManagement[];
  currentSettings: VideoCallManagement | null;
  settingsLoading: boolean;
  settingsError: string | null;
  settingsTotalCount?: number;
  settingsCurrentPage: number;
  settingsHasNextPage: boolean;
  settingsHasPreviousPage: boolean;
  settingsSearchQuery: string;
  
  // Form state
  isFormDirty: boolean;
  lastSavedAt: string | null;
}

const initialState: VideoCallManagementState = {
  // Latest settings
  latestSettings: null,
  latestSettingsLoading: false,
  latestSettingsError: null,
  
  // All settings
  settings: [],
  currentSettings: null,
  settingsLoading: false,
  settingsError: null,
  settingsTotalCount: 0,
  settingsCurrentPage: 1,
  settingsHasNextPage: false,
  settingsHasPreviousPage: false,
  settingsSearchQuery: "",
  
  // Form state
  isFormDirty: false,
  lastSavedAt: null,
};

const videoCallManagementSlice = createSlice({
  name: "videoCallManagement",
  initialState,
  reducers: {
    clearLatestSettingsError(state) { 
      state.latestSettingsError = null; 
    },
    clearSettingsError(state) { 
      state.settingsError = null; 
    },
    clearCurrentSettings(state) { 
      state.currentSettings = null; 
    },
    setSettingsSearchQuery(state, action: PayloadAction<string>) { 
      state.settingsSearchQuery = action.payload; 
    },
    setVideoCallManagementFormDirty(state, action: PayloadAction<boolean>) { 
      state.isFormDirty = action.payload; 
    },
    clearAllSettings(state) {
      state.settings = [];
      state.currentSettings = null;
      state.latestSettings = null;
      state.settingsTotalCount = 0;
    },
    updateLatestSettingsFromForm(state, action: PayloadAction<Partial<VideoCallManagement>>) {
      if (state.latestSettings) {
        state.latestSettings = { ...state.latestSettings, ...action.payload };
      }
    }
  },
  extraReducers: (builder) => {
    // Latest Settings Extra Reducers
    builder
      .addCase(fetchLatestVideoCallManagement.pending, (state) => { 
        state.latestSettingsLoading = true; 
        state.latestSettingsError = null; 
      })
      .addCase(fetchLatestVideoCallManagement.fulfilled, (state, action: PayloadAction<VideoCallManagementLatestResponse>) => { 
        state.latestSettingsLoading = false; 
        state.latestSettings = action.payload; 
        state.lastSavedAt = new Date().toISOString();
      })
      .addCase(fetchLatestVideoCallManagement.rejected, (state, action: any) => { 
        state.latestSettingsLoading = false; 
        state.latestSettingsError = action.payload as string; 
      });

    // All Settings Extra Reducers
    builder
      .addCase(fetchVideoCallManagement.pending, (state) => { 
        state.settingsLoading = true; 
        state.settingsError = null; 
      })
      .addCase(fetchVideoCallManagement.fulfilled, (state, action: PayloadAction<VideoCallManagementResponse>) => { 
        state.settingsLoading = false; 
        state.settings = action.payload.data || []; 
        state.settingsTotalCount = action.payload.totalCount || 0; 
        state.settingsCurrentPage = action.payload.page || 1;
        state.settingsHasNextPage = action.payload.hasNextPage || false;
        state.settingsHasPreviousPage = action.payload.hasPreviousPage || false;
      })
      .addCase(fetchVideoCallManagement.rejected, (state, action: any) => { 
        state.settingsLoading = false; 
        state.settingsError = action.payload as string; 
      });

    builder
      .addCase(fetchVideoCallManagementById.pending, (state) => { 
        state.settingsLoading = true; 
        state.settingsError = null; 
      })
      .addCase(fetchVideoCallManagementById.fulfilled, (state, action: PayloadAction<VideoCallManagement>) => { 
        state.settingsLoading = false; 
        state.currentSettings = action.payload; 
      })
      .addCase(fetchVideoCallManagementById.rejected, (state, action: any) => { 
        state.settingsLoading = false; 
        state.settingsError = action.payload as string; 
      });

    // Create Extra Reducers
    builder
      .addCase(createVideoCallManagement.pending, (state) => { 
        state.settingsLoading = true; 
        state.settingsError = null; 
      })
      .addCase(createVideoCallManagement.fulfilled, (state, action: PayloadAction<VideoCallManagement>) => { 
        state.settingsLoading = false; 
        state.settings.unshift(action.payload); 
        state.settingsTotalCount = (state.settingsTotalCount || 0) + 1; 
        state.latestSettings = action.payload; // Update latest settings
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(createVideoCallManagement.rejected, (state, action: any) => { 
        state.settingsLoading = false; 
        state.settingsError = action.payload as string; 
      });

    // Update Extra Reducers
    builder
      .addCase(updateVideoCallManagement.pending, (state) => { 
        state.settingsLoading = true; 
        state.settingsError = null; 
      })
      .addCase(updateVideoCallManagement.fulfilled, (state, action: PayloadAction<VideoCallManagement>) => {
        state.settingsLoading = false;
        const index = state.settings.findIndex(x => x.id === action.payload.id);
        if (index !== -1) state.settings[index] = action.payload;
        if (state.currentSettings?.id === action.payload.id) state.currentSettings = action.payload;
        state.latestSettings = action.payload; // Update latest settings
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(updateVideoCallManagement.rejected, (state, action: any) => { 
        state.settingsLoading = false; 
        state.settingsError = action.payload as string; 
      });

    // Delete Extra Reducers
    builder
      .addCase(deleteVideoCallManagement.pending, (state) => { 
        state.settingsLoading = true; 
        state.settingsError = null; 
      })
      .addCase(deleteVideoCallManagement.fulfilled, (state, action: PayloadAction<string>) => { 
        state.settingsLoading = false; 
        state.settings = state.settings.filter(x => x.id !== action.payload); 
        if (state.currentSettings?.id === action.payload) state.currentSettings = null; 
        if (state.latestSettings?.id === action.payload) state.latestSettings = null;
        state.settingsTotalCount = Math.max(0, (state.settingsTotalCount || 0) - 1); 
      })
      .addCase(deleteVideoCallManagement.rejected, (state, action: any) => { 
        state.settingsLoading = false; 
        state.settingsError = action.payload as string; 
      });
  }
});

export const { 
  clearLatestSettingsError,
  clearSettingsError, 
  clearCurrentSettings, 
  setSettingsSearchQuery,
  setVideoCallManagementFormDirty,
  clearAllSettings,
  updateLatestSettingsFromForm
} = videoCallManagementSlice.actions;

export default videoCallManagementSlice.reducer;
